import * as React from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { IconButton } from '../../components/Button/Button';
import { sendPartnerEvent } from '../../graphql/mutations';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { useEffect, useState } from 'react';
import { Communicator } from '../../tools/Communication';
import { EditItem, EditSection } from '../../components/Layout';
import ClassicLoadingSpinner from '../../components/LoadingSpinner/ClassicLoadingSpinner';
import { adminInfoStoreWithContactsQuery } from '../../graphql/queries';
import { ContactRole } from '../../graphql/generatedTypes';
import { SalesforceContacts } from './SalesforceContacts';
import { isCommunicator } from './contactUtils';
import { VytalStoreTypes, pickpackTypesByVytalType } from './StoreTypeSelector';
import { ContentSection } from '../../components/Layout/ContentSection';
import { Routes } from '@/utils/routes';
import { Link } from 'react-router-dom';

type SendEmailTabProps = {
  storeTextId: string | null;
  title: string;
};

type User = {
  userName: string;
  password: string;
};

export const SendEmailTab = ({ storeTextId, title }: SendEmailTabProps) => {
  const [sendPartnerEventMutation, { loading }] = useMutation(sendPartnerEvent);
  const [appUsers, setAppUsers] = useState<User[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [store, setStore] = useState<any>(null);
  const [orderInfo, setOrderInfo] = useState<any>(null);

  const { loading: loadingStore } = useQuery(adminInfoStoreWithContactsQuery, {
    skip: !storeTextId,
    variables: { id: storeTextId },
    onCompleted: (data) => {
      if (
        data != null &&
        data.store != null &&
        Array.isArray(data.store.contacts)
      ) {
        setContacts(data.store.contacts.filter((c: any) => isCommunicator(c)));
        setStore(data.store);
      }
    },
  });

  useEffect(() => {
    Communicator.Get('Merchant/GetMerchantStoreByPickpackId', {
      storeId: storeTextId,
    })
      .then((response) => response.json())
      .then((store) => {
        setAppUsers(store.appUsers);
      });
    Communicator.Get('api/3/merchantOrder/GetLastDeliveryAddress', {
      storeTextId,
    })
      .then((response) => response.json())
      .then((orderInfo) => {
        setOrderInfo(orderInfo);
      });
  }, []);

  function sendEvent(name: string, value: string) {
    sendPartnerEventMutation({
      variables: {
        store_id: storeTextId,
        name: name,
        value: value,
        affected_roles: Object.values(ContactRole),
        create_sf_chatter: true,
        create_sf_task: false,
      },
      update: (cache, data) => {
        if (data.data.sendPartnerEvent !== true) {
          alert(i18n.t('SendEmailTab:errorSendEmail', { eventName: name }));
        }
      },
    });
  }

  function getOnboardingTitle() {
    console.log({ store });
    if (store != null && store.type != null) {
      const foundType = Object.entries(pickpackTypesByVytalType).find(
        ([vytalType, pickpackTypes]) => {
          return pickpackTypes.includes(store.type);
        },
      );

      if (foundType) {
        const [vytalType, pickpackTypes] = foundType;
        console.log({
          vytalType,
          pickpackTypes,
          storeType: store.type,
          compare: VytalStoreTypes.Canteen.toString(),
          compareResult: vytalType !== VytalStoreTypes.Canteen.toString(),
        });
        if (vytalType !== VytalStoreTypes.Canteen.toString()) {
          return i18n.t('SendEmailTab:onboardingEmail');
        } else {
          return i18n.t('SendEmailTab:onboardingEmailAndMarketing');
        }
      }
    }
    return i18n.t('SendEmailTab:onboardingEmail');
  }

  function getDeliveryAddress() {
    const deliveryAddress = orderInfo?.deliveryAddress;
    if (deliveryAddress == null) {
      return 'NOT FOUND';
    }
    return `${deliveryAddress.company}, ${
      deliveryAddress.firstName != null ? deliveryAddress.firstName + ' ' : ''
    }${deliveryAddress.lastName != null ? deliveryAddress.lastName : ''}, ${
      deliveryAddress.streetname
    } ${deliveryAddress.streetno}, ${deliveryAddress.zip} ${
      deliveryAddress.city
    }, ${deliveryAddress.country || 'DE'}`;
  }

  if (loadingStore || storeTextId == null) {
    return <ClassicLoadingSpinner />;
  }

  return (
    <ContentSection title={title} collapsable={false} key="content">
      <EditSection>
        <p>{i18n.t('SendEmailTab:info')}</p>
        {contacts.length === 0 && (
          <p>{i18n.t('SendEmailTab:noCommunicator')}</p>
        )}
        <SalesforceContacts contacts={contacts} storeId={null} />
        <EditItem
          title={getOnboardingTitle()}
          helpText={i18n.t('SendEmailTab:onboardingEmailHelpText')}
        >
          <IconButton
            disabled={
              appUsers == null || appUsers.length < 1 || contacts.length === 0
            }
            isLoading={loading}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() =>
              sendEvent(
                'send_onboarding_email',
                JSON.stringify({
                  user_name: appUsers[0].userName,
                  password: appUsers[0].password,
                }),
              )
            }
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:accountManagementNps')}
          helpText={i18n.t('SendEmailTab:accountManagementNpsHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('account_management_nps', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:problemReturn')}
          helpText={i18n.t('SendEmailTab:problemReturnHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('problem_return', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:problemOpeningHours')}
          helpText={i18n.t('SendEmailTab:problemOpeningHoursHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('problem_opening_hours', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:problemOrder')}
          helpText={i18n.t('SendEmailTab:problemOrderHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('problem_order', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:reducedDelivery')}
          helpText={i18n.t('SendEmailTab:reducedDeliveryHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('reduced_delivery', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:cancelledDelivery')}
          helpText={i18n.t('SendEmailTab:cancelledDeliveryHelpText')}
        >
          <IconButton
            isLoading={loading}
            disabled={contacts.length === 0}
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() => sendEvent('cancelled_delivery', '')}
          ></IconButton>
        </EditItem>
        <EditItem
          title={i18n.t('SendEmailTab:undeliverableDelivery')}
          helpText={i18n.t('SendEmailTab:undeliverableDeliveryHelpText')}
        >
          {getDeliveryAddress() === 'NOT FOUND' && (
            <p>{i18n.t('SendEmailTab:noDeliveryAddress')}</p>
          )}
          {getDeliveryAddress() !== 'NOT FOUND' && (
            <div>
              <Link to={Routes.orderDetails(orderInfo?.orderId)}>
                {i18n.t('SendEmailTab:orderLink')}
              </Link>
              <p>
                {i18n.t('SendEmailTab:sendEmailWith', {
                  deliveryAddress: getDeliveryAddress(),
                  executedOn: new Date(
                    orderInfo?.orderExecutionDate,
                  ).toLocaleDateString(
                    i18n.language,
                    getDateFormatOptions(false),
                  ),
                })}
              </p>
            </div>
          )}
          <IconButton
            isLoading={loading}
            disabled={
              contacts.length === 0 || getDeliveryAddress() === 'NOT FOUND'
            }
            label={i18n.t('SendEmailTab:sendEmail')}
            onClick={() =>
              sendEvent(
                'undeliverable_delivery',
                JSON.stringify({
                  delivery_address: getDeliveryAddress(),
                }),
              )
            }
          ></IconButton>
        </EditItem>
      </EditSection>
    </ContentSection>
  );
};
